import { Pause, Play, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiDelete, apiGet, apiPatch, apiPost } from '../api/client.js';
import { AlertRuleForm, type AlertRuleValues } from '../components/AlertRuleForm.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { PageHeader } from '../components/PageHeader.js';

interface Rule {
  id: string;
  name: string;
  scheduleCron: string;
  signals: string;
  channels: string;
  enabled: boolean;
  lastRunAt: string | null;
}

export function AutomationPage(): JSX.Element {
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [creating, setCreating] = useState(false);

  const load = (): void => {
    apiGet<{ rules: Rule[] }>('/api/alerts/rules').then((r) => setRules(r.rules));
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (v: AlertRuleValues): Promise<void> => {
    await apiPost('/api/alerts/rules', v);
    setCreating(false);
    load();
  };

  const toggle = async (id: string, enabled: boolean): Promise<void> => {
    await apiPatch(`/api/alerts/rules/${id}`, { enabled: !enabled });
    load();
  };

  const runNow = async (id: string): Promise<void> => {
    await apiPost(`/api/alerts/rules/${id}/run`, {});
    load();
  };

  const remove = async (id: string): Promise<void> => {
    if (!confirm('Delete this rule?')) return;
    await apiDelete(`/api/alerts/rules/${id}`);
    load();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        title="Automation"
        accent="rules"
        sub="Scheduled rules that watch your stores and send digests when something needs attention."
        actions={
          !creating ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              New alert rule
            </Button>
          ) : null
        }
      />

      {creating && (
        <Card title="New alert rule">
          <AlertRuleForm onSubmit={create} onCancel={() => setCreating(false)} />
        </Card>
      )}

      {rules === null ? (
        <Card>
          <p style={{ color: 'var(--c-muted)' }}>Loading rules…</p>
        </Card>
      ) : rules.length === 0 && !creating ? (
        <Card>
          <p style={{ fontSize: 'var(--text-md)', color: 'var(--c-muted)' }}>
            No alert rules yet. Create one to get a weekly digest of underperforming listings.
          </p>
        </Card>
      ) : (
        rules.map((r) => (
          <Card
            key={r.id}
            title={r.name}
            description={`Schedule ${r.scheduleCron} · Last run ${
              r.lastRunAt ? new Date(r.lastRunAt).toLocaleString() : 'never'
            }`}
            action={
              <span
                style={{
                  fontSize: 'var(--text-xs)',
                  padding: '3px 8px',
                  borderRadius: 99,
                  background: r.enabled ? 'var(--c-success-bg)' : 'var(--c-divider)',
                  color: r.enabled ? 'var(--c-success)' : 'var(--c-muted)',
                  fontWeight: 600,
                }}
              >
                {r.enabled ? 'Enabled' : 'Disabled'}
              </span>
            }
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button size="sm" icon={Play} onClick={() => runNow(r.id)}>
                Run now
              </Button>
              <Button
                size="sm"
                variant="secondary"
                icon={r.enabled ? Pause : Play}
                onClick={() => toggle(r.id, r.enabled)}
              >
                {r.enabled ? 'Disable' : 'Enable'}
              </Button>
              <Button size="sm" variant="danger" icon={Trash2} onClick={() => remove(r.id)}>
                Delete
              </Button>
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
